/**
 * resolveCountScaledChoice move — resolves a pending count-scaled choose-one
 * player choice (WP-675 / D-24490).
 *
 * Called by the active player (or the deterministic bot) after a count-scaled-choose
 * hero ability parked a PendingCountScaledChoice on G.pendingCountScaledChoice (FIFO).
 * This realizes vnom's Symbiotic Adaptation: "Choose one: +1 recruit for each other
 * card you played this turn with a recruit icon / Or +1 attack for each other card
 * with an attack icon." The player picks an option index; the chosen option's grant
 * reuses the shipped attack-per-count / recruit-per-count executor (no re-implementation
 * of the count-scaled grant).
 *
 * The two options differ in BOTH resource and count, so the choice is a genuine
 * interactive pending choice (the draw-or-empowered pattern), not an oracle-max.
 *
 * Atomicity: the chosen effect fires, then the front entry is popped. A failed
 * validation (out-of-range index, empty queue, wrong player) returns before any
 * mutation and leaves the queue intact.
 *
 * No registry imports. No .reduce(). Moves never throw.
 */
import { executeSingleEffect } from '../hero/heroEffects.execute.js';
import { gainOfficerToDiscard } from './recruitOfficer.js';
import { pushLog } from '../log/logPush.js';
/**
 * Whether any count-scaled choose-one choice is currently pending.
 *
 * Single predicate imported by the block-all action-move guards and the
 * getLegalMoves short-circuit. `undefined` and `[]` both mean no pending choice
 * (mirrors hasPendingDrawOrEmpowered, D-24069).
 *
 * // why: pendingCountScaledChoice is lazy-init (D-24490); undefined and [] both mean no pending choice
 *
 * @param G - The game state to inspect (not mutated).
 * @returns true when the pending count-scaled-choice queue holds at least one entry.
 */
export function hasPendingCountScaledChoice(G) {
    return (G.pendingCountScaledChoice?.length ?? 0) > 0;
}
/**
 * Resolves the FRONT pending count-scaled choose-one choice.
 *
 * Atomic sequence (HARD — exact order, mirrors drawOrEmpowered.resolve.ts):
 *   1. Validate args — optionIndex must be an integer in range of the front entry's
 *      options; anything else is a silent no-op (queue intact).
 *   2. Validate the front pending entry — non-empty queue, front.playerID match.
 *   3. Dispatch the chosen option by REUSING the existing per-count executor (no re-impl):
 *      an 'attack' option → executeSingleEffect(..., { type: 'attack-per-count', … }),
 *      a 'recruit' option → executeSingleEffect(..., { type: 'recruit-per-count', … }).
 *      The triggering card id (front.cardId) is threaded so the icon count source
 *      excludes the played vnom card from its own count.
 *   4. Front-pop (queue.shift()) LAST.
 *
 * Any failure before step 3 ABORTS the grant (no shift). Moves never throw.
 *
 * // why: block-all guard — no other move may fire while a count-scaled choice is outstanding (D-24490)
 *
 * @param context - boardgame.io move context; the rest (ctx, events, random, log) is
 *   spread into `context` and forwarded to the per-count executor.
 * @param args - the chosen option index.
 */
export function resolveCountScaledChoice({ G, playerID, ...context }, args) {
    // Step 1a: Validate the front pending entry FIRST (so the range check can read its options).
    // why: pendingCountScaledChoice is lazy-init (D-24490); undefined and [] both mean no pending choice
    const queue = G.pendingCountScaledChoice;
    if (queue === undefined || queue.length === 0) {
        return;
    }
    const front = queue[0];
    if (front.playerID !== playerID) {
        return;
    }
    // Step 1b: Validate args — optionIndex must be an integer index into front.options.
    // why: a client may submit the move with no payload (undefined) or null; reading a field off
    // either throws a TypeError, and moves never throw — reject before any field read.
    if (args === null || typeof args !== 'object') {
        return;
    }
    const optionIndex = args.optionIndex;
    if (typeof optionIndex !== 'number'
        || !Number.isInteger(optionIndex)
        || optionIndex < 0
        || optionIndex >= front.options.length) {
        return;
    }
    const chosen = front.options[optionIndex];
    // Step 3: Dispatch the chosen option by REUSING the existing executor, dispatched by kind
    // (WP-679 / D-24495 — heterogeneous options; no re-implementation of any grant; the
    // gain-officer / flat-attack kinds are D-24696).
    if (chosen.kind === 'undercover') {
        // why: WP-679 — the Undercover option dispatches WP-678's source-shape executor, which
        // sends a card to the Victory Pile (and, for the hand shape with ≥2 eligible, parks its
        // OWN nested PendingUndercoverChoice — a legal nested pick resolved by resolveUndercoverChoice).
        const undercoverType = chosen.source === 'hand-shield-hero'
            ? 'undercover-hand-shield-hero'
            : 'undercover-officer-stack';
        executeSingleEffect(G, context, playerID, front.cardId, { type: undercoverType });
    }
    else if (chosen.kind === 'gain-officer') {
        // why: D-24696 — Spy Network's "Gain a S.H.I.E.L.D. Officer": the top Officer goes to the
        // chooser's DISCARD (rules v23 "Gain" = put it into your discard pile). An empty Officer
        // stack makes the chosen option a logged no-op (the gain-officer-current precedent).
        const triggeringCard = front.cardId;
        if (gainOfficerToDiscard(G, playerID)) {
            pushLog(G, `Player ${playerID} gained a S.H.I.E.L.D. Officer.`, 'applied', triggeringCard);
        }
        else {
            pushLog(G, `Player ${playerID} chose to gain a S.H.I.E.L.D. Officer, but the Officer stack is empty.`, 'blocked', triggeringCard);
        }
    }
    else if (chosen.kind === 'flat-attack') {
        // why: D-24696 — Spy Network's "you get +2[icon:attack]": reuse the shipped flat `attack`
        // executor (it applies the grant and logs it), no re-implementation.
        executeSingleEffect(G, context, playerID, front.cardId, {
            type: 'attack',
            magnitude: chosen.magnitude,
        });
    }
    else if (chosen.kind === 'flat-recruit') {
        // why: WP-856 / D-24792 — Possessed by Brother's Spirit's "+4[icon:recruit]": reuse the
        // shipped flat `recruit` executor (the flat-attack mirror above), no re-implementation.
        executeSingleEffect(G, context, playerID, front.cardId, {
            type: 'recruit',
            magnitude: chosen.magnitude,
        });
    }
    else {
        // why: WP-675 / D-24490 — the count-scaled option maps to the shipped per-count effect
        // type; front.cardId is threaded so an "each OTHER card" source excludes the played card.
        // why: WP-677/679 — perEach (the "for each N" divisor) is threaded so the grant is
        // magnitude × floor(count / perEach) ("+1 attack for each 2 S.H.I.E.L.D. Levels").
        const effectType = chosen.resource === 'attack' ? 'attack-per-count' : 'recruit-per-count';
        executeSingleEffect(G, context, playerID, front.cardId, {
            type: effectType,
            magnitude: chosen.magnitude,
            countSource: chosen.countSource,
            ...(chosen.perEach !== undefined ? { perEach: chosen.perEach } : {}),
        });
    }
    // Step 4: Front-pop LAST (front-pop = Array.shift), mirroring WP-286.
    queue.shift();
}
//# sourceMappingURL=countScaledChoice.resolve.js.map